import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Popover,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  ErrorOutline,
  Publish,
  Search,
  Sync,
  VisibilityOff,
} from '@material-ui/icons';
import clsx from 'clsx';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import { useOperatorState } from '../home/hooks/useOperatorState';
import { useCatalogSync } from '../home/hooks/useCatalogSync';
import { catalogSourcePhaseLabel } from '../catalog-sync/catalogSourcePhase';
import { deriveChecklistSteps } from '../home/deriveChecklistSteps';
import { shouldShowOperatorDashboard } from '../home/homeMode';
import { useBundleRebuild } from '../home/components/Diagnostics/useBundleRebuild';
import PublishProgressDialog from '../home/components/PublishProgressDialog';
import {
  useOperationDialogVisibility,
} from '../home/components/PublishProgressDialog/useOperationDialogVisibility';
import { usePreviewOpener } from '../shell/preview/usePreviewOpener';
import {
  DiagnosticsSeverity,
  IndexingStatus,
  LiveStatus,
  PublishState,
} from '../home/operatorState';
import styles from './OperationStatusPills.module.scss';

export function buildOperationIndicators({ operatorState, catalogSync, localizeMessage }) {
  const indicators = [];
  const indexingRunning = catalogSync?.status === IndexingStatus.RUNNING
    || operatorState.indexing === IndexingStatus.RUNNING;
  const reindexRequired = catalogSync?.reindexRequired === true;
  const publishBlocked = reindexRequired || indexingRunning;
  const progress = catalogSync?.progressKnown !== false && typeof catalogSync?.progress === 'number'
    ? catalogSync.progress : null;

  if (indexingRunning) {
    indicators.push({
      key: 'indexing',
      severity: 'info',
      icon: <Sync />,
      label: catalogSourcePhaseLabel(catalogSync, localizeMessage) || (progress !== null && progress >= 100
        ? localizeMessage('operator.supervisor.indicator.indexing_finalizing')
        : progress !== null
          ? `${localizeMessage('operator.supervisor.indicator.indexing')} ${progress}%`
          : localizeMessage('operator.supervisor.indicator.indexing')),
      title: localizeMessage('operator.supervisor.indexing.title'),
      description: localizeMessage('operator.supervisor.indexing.description'),
    });
  } else if (reindexRequired) {
    indicators.push({
      key: 'reindex-required',
      severity: 'warning',
      icon: <Sync />,
      label: localizeMessage('operator.supervisor.indicator.reindex'),
      title: localizeMessage('operator.supervisor.reindex.title'),
      description: localizeMessage('operator.supervisor.reindex.description'),
    });
  }

  if (operatorState.publishState === PublishState.UNPUBLISHED_CHANGES) {
    indicators.push({
      key: 'publish',
      severity: 'warning',
      icon: <Publish />,
      label: localizeMessage('operator.supervisor.indicator.publish'),
      title: localizeMessage('operator.supervisor.publish.title'),
      description: localizeMessage('operator.supervisor.publish.description'),
      actionDisabled: publishBlocked,
    });
  } else if (
    operatorState.live === LiveStatus.NOT_ACTIVE
    && operatorState.publishState !== PublishState.NOT_PUBLISHED
  ) {
    indicators.push({
      key: 'live-off',
      severity: 'warning',
      icon: <VisibilityOff />,
      label: localizeMessage('operator.supervisor.indicator.live_off'),
      title: localizeMessage('operator.supervisor.liveOff.title'),
      description: localizeMessage('operator.supervisor.liveOff.description'),
    });
  }

  if (
    operatorState.diagnosticsSeverity === DiagnosticsSeverity.WARNING
    || operatorState.diagnosticsSeverity === DiagnosticsSeverity.ERROR
  ) {
    indicators.push({
      key: 'diagnostics',
      severity: operatorState.diagnosticsSeverity,
      icon: <ErrorOutline />,
      label: localizeMessage('operator.supervisor.indicator.diagnostics'),
      compact: true,
      title: localizeMessage('operator.supervisor.diagnostics.title'),
      description: localizeMessage('operator.supervisor.diagnostics.description'),
    });
  }

  return indicators;
}

export default function OperationStatusPills({ shopId }) {
  const history = useHistory();
  const localizeMessage = useLocalizedMessage();
  const { operatorState, isLoading } = useOperatorState();
  const catalogSync = useCatalogSync();
  const publishBuild = useBundleRebuild();
  const { previewShopAddress, openStorefrontPreview } = usePreviewOpener();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeIndicator, setActiveIndicator] = useState(null);
  const publishDialog = useOperationDialogVisibility(publishBuild.status);
  const showOperatingStatus = useMemo(() => shouldShowOperatorDashboard(
    operatorState,
    deriveChecklistSteps(operatorState),
  ), [operatorState]);
  const publishPending = publishBuild.status === 'queued' || publishBuild.status === 'running';
  const publishBlocked = catalogSync.reindexRequired === true
    || catalogSync.status === IndexingStatus.RUNNING
    || operatorState.indexing === IndexingStatus.RUNNING;

  const indicators = useMemo(() => (
    isLoading || !showOperatingStatus
      ? []
      : buildOperationIndicators({ operatorState, catalogSync, localizeMessage })
  ), [catalogSync, isLoading, localizeMessage, operatorState, showOperatingStatus]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setActiveIndicator(null);
  }, []);

  const publish = useCallback(() => {
    if (publishBlocked) return;
    if (typeof publishBuild.reset === 'function') {
      publishBuild.reset();
    }
    publishDialog.begin();
    handleClose();
    Promise.resolve(publishBuild.startBuildAndPromote(shopId)).catch(() => {});
  }, [handleClose, publishBlocked, publishBuild, publishDialog, shopId]);

  const startRequiredReindex = useCallback(() => {
    handleClose();
    catalogSync.startFullUpdate();
  }, [catalogSync, handleClose]);

  const handleOpen = useCallback((event, indicator) => {
    setAnchorEl(event.currentTarget);
    setActiveIndicator(indicator);
  }, []);

  if (!indicators.length && !publishDialog.open) return null;

  const pillClass = (indicator) => {
    if (indicator.severity === 'error') return styles.pillError;
    if (indicator.severity === 'warning') return styles.pillWarning;
    return styles.pillInfo;
  };

  const stateMarkClass = (indicator) => {
    if (indicator.severity === 'error') return styles.stateMarkError;
    if (indicator.severity === 'warning') return styles.stateMarkWarning;
    return styles.stateMarkInfo;
  };

  const renderPill = (indicator) => {
    const content = (
      <ButtonBase
        key={indicator.key}
        className={clsx(
          styles.pill,
          pillClass(indicator),
          indicator.key === 'publish' && styles.pillPublish,
          indicator.compact && styles.pillCompact,
        )}
        onClick={(event) => handleOpen(event, indicator)}
        disabled={indicator.key === 'publish' && publishPending}
        data-testid={`operation-pill-${indicator.key}`}
        aria-label={indicator.label}
        aria-haspopup="dialog"
        aria-expanded={Boolean(anchorEl && activeIndicator?.key === indicator.key)}
        aria-busy={indicator.key === 'indexing' || undefined}
      >
        {indicator.key === 'indexing' || (indicator.key === 'publish' && publishPending)
          ? <CircularProgress size={14} color="inherit" />
          : indicator.icon}
        {!indicator.compact && (
          <span className={styles.pillText}>{indicator.label}</span>
        )}
      </ButtonBase>
    );

    if (!indicator.compact) return content;
    return (
      <Tooltip key={indicator.key} title={indicator.label}>
        {content}
      </Tooltip>
    );
  };

  return (
    <>
      {indicators.length > 0 && (
        <Box
          className={styles.root}
          data-testid="operation-status-pills"
          aria-live="polite"
          aria-atomic="true"
        >
          {indicators.map(renderPill)}
        </Box>
      )}

      {indicators.length > 0 && (
        <Popover
          open={Boolean(anchorEl && activeIndicator)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ className: styles.popoverPaper }}
        >
          {activeIndicator && (
            <Box
              className={styles.popover}
              role="dialog"
              aria-labelledby={`operation-${activeIndicator.key}-title`}
              aria-describedby={`operation-${activeIndicator.key}-description`}
            >
            <Box className={styles.popoverHeader}>
              <Box className={clsx(styles.stateMark, stateMarkClass(activeIndicator))} aria-hidden="true">
                {activeIndicator.icon}
              </Box>
              <Box className={styles.popoverCopy}>
                <Typography
                  id={`operation-${activeIndicator.key}-title`}
                  component="h2"
                  className={styles.popoverTitle}
                >
                  {activeIndicator.title}
                </Typography>
                <Typography
                  id={`operation-${activeIndicator.key}-description`}
                  component="p"
                  className={styles.popoverDescription}
                >
                  {activeIndicator.description}
                </Typography>
              </Box>
            </Box>
            <Box
              className={clsx(
                styles.actions,
                (activeIndicator.key === 'live-off'
                  || activeIndicator.key === 'reindex-required')
                  && styles.actionStack,
              )}
            >
              {activeIndicator.key === 'diagnostics' && (
                <Button
                  size="small"
                  onClick={() => {
                    handleClose();
                    history.push(`/app/${shopId}/diagnostics`);
                  }}
                >
                  {localizeMessage('operator.supervisor.open_diagnostics')}
                </Button>
              )}
              {activeIndicator.key === 'live-off' && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={!previewShopAddress}
                    onClick={() => {
                      handleClose();
                      openStorefrontPreview();
                    }}
                    startIcon={<Search />}
                  >
                    {localizeMessage('operator.supervisor.open_preview')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={publishBlocked}
                    onClick={publish}
                    startIcon={<Publish />}
                  >
                    {localizeMessage('operator.supervisor.publish_cta')}
                  </Button>
                </>
              )}
              {activeIndicator.key === 'reindex-required' && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={startRequiredReindex}
                  startIcon={<Sync />}
                  data-testid="operation-reindex-cta"
                >
                  {localizeMessage('operator.supervisor.reindex_cta')}
                </Button>
              )}
              {activeIndicator.key === 'publish' && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={activeIndicator.actionDisabled || publishPending}
                  onClick={publish}
                  startIcon={<Publish />}
                  data-testid="operation-publish-cta"
                >
                  {localizeMessage('operator.supervisor.publish_cta')}
                </Button>
              )}
              {activeIndicator.key === 'indexing' && (
                <Button
                  size="small"
                  onClick={() => {
                    handleClose();
                    history.push(`/app/${shopId}/home`);
                  }}
                >
                  {localizeMessage('operator.supervisor.open_home')}
                </Button>
              )}
            </Box>
            </Box>
          )}
        </Popover>
      )}

      <PublishProgressDialog
        open={publishDialog.open}
        operation={publishBuild.operation || 'production'}
        status={publishBuild.status}
        error={publishBuild.error}
        onRetry={publish}
        onClose={publishDialog.close}
        localizeMessage={localizeMessage}
      />
    </>
  );
}

OperationStatusPills.propTypes = {
  shopId: PropTypes.string.isRequired,
};
